import { z } from "zod";

import { type OfferStatus, OfferType } from "~/lib/schemas/db-enums";
import {
  invalidAmountMessage,
  isValidAmountForCurrency,
} from "~/lib/shared/currency";

export const OfferSchema = z
  .object({
    id: z.string(),
    coachId: z.string(),
    username: z.string(),

    type: z.nativeEnum(OfferType),
    summitId: z.string().uuid().nullable().optional(),
    summitSessionId: z.string().uuid().nullable().optional(),
    platformFeeBps: z.number().int().min(0).max(10000).nullable().optional(),

    directSaleEnabled: z.boolean().nullable().optional(),
    price: z.number().min(0).nullable().optional(), // store as decimal, convert to cents in action
    currency: z.string().length(3).nullable().optional(), // e.g., "USD"
    originalPrice: z.number().min(0).nullable().optional(), // store as decimal, convert to cents in action

    preHeadline: z.string().nullable().optional(),
    headline: z.string().max(100).nullable().optional(),
    description: z.string().max(300).nullable().optional(),

    tags: z
      .array(
        z.object({
          _delete: z.boolean().optional(),
          id: z.string().optional(),
          offerId: z.string().optional(),
          property: z.string(),
          title: z.string().max(20),
          order: z.number().optional(),
        }),
      )
      .optional(),
    benefits: z
      .array(
        z.object({
          _delete: z.boolean().optional(),
          id: z.string().optional(),
          offerId: z.string().optional(),
          title: z.string(),
          content: z.string().nullable().optional(),
          // Stored/uploaded image URL — accept any string so a legacy or moved
          // host value can never block saving (matches the other image fields).
          imageSrc: z.string().nullable().optional(),
          order: z.number().optional(),
        }),
      )
      .optional(),

    testimonials: z
      .array(
        z.object({
          _delete: z.boolean().optional(),
          id: z.string().optional(),
          content: z.string(),
          author: z.string(),
          order: z.number().optional(),
        }),
      )
      .optional(),

    components: z
      .array(
        z.object({
          _delete: z.boolean().optional(),
          id: z.string().optional(),
          preHeader: z.string().nullable().optional(),
          title: z.string(),
          imageUrl: z.string().nullable().optional(),
          content: z.string(),
          order: z.number().optional(),
        }),
      )
      .optional(),

    vslVideoUrl: z.string().nullable().optional(),

    strugglesHeadline: z.string().nullable().optional(),
    strugglesDescription: z.string().nullable().optional(),
    strugglesImageUrl: z.string().nullable().optional(),

    resultsPreHeadline: z.string().nullable().optional(),
    resultsHeadline: z.string().nullable().optional(),
    resultsDescription: z.string().nullable().optional(),
    resultsImageUrl: z.string().nullable().optional(),

    programHeadline: z.string().nullable().optional(),
    programDescription: z.string().nullable().optional(),
    programImageUrl: z.string().nullable().optional(),

    bottomCtaHeadline: z.string().nullable().optional(),
    bottomCtaText: z.string().nullable().optional(),

    ctaUrl: z
      .string()
      .nullable()
      .optional()
      .refine(
        (val) => !val || !/^(javascript|data|vbscript):/iu.test(val.trim()),
        { message: "Unsafe URL protocol" },
      ),

    applicationDeadline: z
      .union([z.literal(""), z.string().date()])
      .nullable()
      .optional(),
    cohortStartDate: z
      .union([z.literal(""), z.string().date()])
      .nullable()
      .optional(),

    satisfactionGuaranteeText: z.string().nullable().optional(),

    bonuses: z
      .array(
        z.object({
          _delete: z.boolean().optional(),
          id: z.string().optional(),
          offerId: z.string().optional(),
          title: z.string(),
          content: z.string(),
          // Stored/uploaded image URL — accept any string so a legacy or moved
          // host value can never block saving (matches the other image fields).
          imageSrc: z.string().nullable().optional(),
          order: z.number().optional(),
        }),
      )
      .optional(),

    faqs: z
      .array(
        z.object({
          _delete: z.boolean().optional(),
          id: z.string().optional(),
          offerId: z.string().optional(),
          question: z.string(),
          answer: z.string(),
          order: z.number().optional(),
        }),
      )
      .optional(),

    durationLabel: z.string().nullable().optional(),

    // Mini Sprint — coach-editable hero eyebrow/badge. Null → auto-generated
    // from the duration + curriculum length ("Free 5-Day Mini Sprint").
    heroEyebrow: z.string().max(60).nullable().optional(),

    // Mini Sprint — "What you walk away with" callout (the named artifact).
    sprintTakeaway: z.string().nullable().optional(),

    curriculum: z
      .array(
        z.object({
          _delete: z.boolean().optional(),
          // Client-only stable identity for React keys on newly-added rows
          // (they have no DB id yet). Stripped before persisting.
          _key: z.string().optional(),
          id: z.string().optional(),
          offerId: z.string().optional(),
          title: z.string(),
          content: z.string(),
          order: z.number().optional(),
        }),
      )
      .optional(),
  })
  .superRefine((values, ctx) => {
    if (values.type === OfferType.Summit && !values.summitId) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["summitId"],
        message: "Summit specials must be linked to an active summit.",
      });
    }

    // A form without a currency keeps the stored one; USD is only the check's
    // stand-in, and the server checks again against the stored currency.
    const currency = values.currency ?? "USD";
    for (const field of ["price", "originalPrice"] as const) {
      const amount = values[field];
      if (!amount || isValidAmountForCurrency(amount, currency)) continue;
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [field],
        message: invalidAmountMessage(currency),
      });
    }
  });

export type OfferFormData = z.infer<typeof OfferSchema>;

/**
 * What the offer editor loads: the editable fields plus the lifecycle state
 * its status control starts from. `OfferSchema` strips both on save; status
 * changes only through `~/lib/server/offer-lifecycle`.
 */
export type OfferEditData = OfferFormData & {
  status: OfferStatus;
  isMain: boolean;
};
